import { INode } from './types';

/**
 * Helper di sola visualizzazione: non cambiano mai i dati né i nomi su disco.
 */

/** Nome della cartella creata dal backend per l'archivio delle conversazioni (videochat/services/conversation_archive.py). */
export const CALL_ARCHIVE_ROOT_NAME = 'Conversazioni chiamate';

const ARCHIVE_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const LOCALES: { [lang: string]: string } = {
  it: 'it-IT',
  en: 'en-GB',
  es: 'es-ES',
  de: 'de-DE',
};

/** Locale Intl a partire dalla lingua di ngx-translate (default italiano). */
export function localeFor(lang?: string | null): string {
  return LOCALES[(lang || 'it').slice(0, 2)] || 'it-IT';
}

/** Data locale (senza slittamenti di fuso) se il nome è una cartella archivio giornaliera AAAA-MM-GG. */
function archiveDate(name: string): Date | null {
  const m = ARCHIVE_DATE_RE.exec(name || '');
  if (!m) {
    return null;
  }
  const year = +m[1];
  const month = +m[2];
  const day = +m[3];
  const date = new Date(year, month - 1, day);
  // scarta date impossibili (es. 2026-02-31)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** "2026-10-23" -> "23 ottobre 2026"; qualsiasi altro nome resta invariato. */
export function displayFolderName(name: string, lang?: string | null): string {
  const date = archiveDate(name);
  if (!date) {
    return name;
  }
  return new Intl.DateTimeFormat(localeFor(lang), { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/** Cartella radice dell'archivio conversazioni: ha un aspetto dedicato. */
export function isCallArchiveRoot(node?: INode | null): boolean {
  if (!node || node.isLeaf) {
    return false;
  }
  const parent = node.data?.['parent'];
  return !parent && (node.data?.['name'] ?? node.name) === CALL_ARCHIVE_ROOT_NAME;
}

/** Dimensione leggibile: il backend salva i byte del file cifrato. */
export function formatFileSize(bytes: unknown, lang?: string | null): string {
  const value = Number(bytes);
  if (!value || value <= 0 || !isFinite(value)) {
    return '—';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit++;
  }
  const digits = unit === 0 || size >= 10 ? 0 : 1;
  return `${new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: digits }).format(size)} ${units[unit]}`;
}

/** Data e ora brevi ("20 ott 2026, 12:06"); "—" se assente o non valida. */
export function formatModified(value: unknown, lang?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value as string);
  if (isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat(localeFor(lang), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export type FileTone = 'pdf' | 'image' | 'doc' | 'sheet' | 'other';

export interface FileKind {
  /** sigla mostrata nel riquadro colorato (PDF, PNG, DOC…) */
  label: string;
  /** famiglia per il colore del riquadro */
  tone: FileTone;
  /** chiave di traduzione del tipo per la colonna "Tipo" */
  typeKey: string;
}

/** Tipo di file a partire da content-type ed estensione. */
export function fileKind(node: INode): FileKind {
  const contentType = (node.data?.['content_type'] || '').toString().toLowerCase();
  const name = (node.data?.['name'] || node.name || '').toString();
  const dot = name.lastIndexOf('.');
  const ext = dot > -1 ? name.slice(dot + 1).toLowerCase() : '';

  if (contentType === 'application/pdf' || ext === 'pdf') {
    return { label: 'PDF', tone: 'pdf', typeKey: 'filemanager.ui.type_pdf' };
  }
  if (contentType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    const sub = ext || contentType.replace('image/', '');
    return { label: (sub === 'jpeg' ? 'JPG' : sub).toUpperCase().slice(0, 4), tone: 'image', typeKey: 'filemanager.ui.type_image' };
  }
  if (contentType.includes('word') || ['doc', 'docx'].includes(ext)) {
    return { label: 'DOC', tone: 'doc', typeKey: 'filemanager.ui.type_doc' };
  }
  if (contentType.includes('csv') || contentType.includes('excel') || contentType.includes('sheet') || ['csv', 'xls', 'xlsx'].includes(ext)) {
    return { label: (ext || 'CSV').toUpperCase().slice(0, 4), tone: 'sheet', typeKey: 'filemanager.ui.type_sheet' };
  }
  return { label: (ext || 'FILE').toUpperCase().slice(0, 4), tone: 'other', typeKey: 'filemanager.ui.type_file' };
}
